import type { CSSProperties } from 'react'
import { useTranslation } from 'react-i18next'
import { Fab, Badge } from '@mui/material'
import AssignmentIcon from '@mui/icons-material/Assignment'
import { useNotes } from '@/hooks/useNotes'
import { useActiveSharedView } from '@/hooks/useActiveSharedView'
import { useCustomViewNotes } from '@/hooks/useCustomViewNotes'
import type { ContentType } from '@/types/api'

interface NotesFabProps {
  contentType: ContentType
  entityId: number
  noteOpen: boolean
  /**
   * True when the button is rendered inside the docked note panel. It is then
   * positioned against the panel's own top edge, so resizing the panel moves
   * the button in the same layout pass — no lag, no transition of its own.
   */
  anchored?: boolean
  onToggle: () => void
}

/** Button diameter (MUI `size="medium"`) plus the gap kept above the panel. */
const FAB_SIZE = 48
const FAB_GAP = 16

export function NotesFab({ contentType, entityId, noteOpen, anchored = false, onToggle }: NotesFabProps) {
  const { t } = useTranslation()
  const { getNotesFor } = useNotes()
  // Either scope earns the badge. A shared-view note only counts while that view is
  // the selected one, which is exactly what useActiveSharedView resolves to.
  const sharedView = useActiveSharedView()
  const viewNotes = useCustomViewNotes(sharedView?.id ?? null, sharedView !== null, `${contentType}:${entityId}`)
  const hasNote =
    getNotesFor(contentType, entityId).length > 0 ||
    viewNotes.getNoteFor(contentType, entityId) !== null

  const label = noteOpen
    ? t('notes.hideNotes')
    : hasNote
      ? t('notes.showNotes')
      : t('notes.addNote')

  // Anchored: absolutely positioned against the panel it lives in, sitting just
  // clear of its top edge. Otherwise: fixed, just above the footer.
  // --notes-fab-right tightens the inset at compact; --safe-bottom keeps the fixed
  // variant clear of the iOS home indicator.
  const position: CSSProperties = anchored
    ? { position: 'absolute', top: -(FAB_SIZE + FAB_GAP), right: 'var(--notes-fab-right, 24px)' }
    : { position: 'fixed', bottom: `calc(var(--footer-height) + ${FAB_GAP}px + var(--safe-bottom))`, right: 'var(--notes-fab-right, 24px)' }

  return (
    // The wrapper establishes the anchor point without creating a stacking context,
    // so the Badge dot (absolutely positioned inside) always paints above the Fab.
    <span
      style={{
        ...position,
        zIndex: 300,
        display: 'inline-flex',
      }}
    >
      <Badge
        variant="dot"
        color="error"
        invisible={!hasNote}
        overlap="circular"
        sx={{ '& .MuiBadge-badge': { zIndex: 9999 } }}
      >
        <Fab
          size="medium"
          color={noteOpen ? 'primary' : 'default'}
          onClick={onToggle}
          aria-label={label}
          title={label}
          data-tour="notes-fab"
        >
          <AssignmentIcon />
        </Fab>
      </Badge>
    </span>
  )
}
